import { CATEGORIES, COLOR_POOL } from "$data/categories";
import type { Category, Element, Pulse, Prompt } from "$data/types";

/** Fisher-Yates shuffle returning a new array. */
export function shuffle<T>(arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Mulberry32 — deterministic PRNG used for daily seeded Pulses. */
export function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic shuffle driven by a Mulberry32 RNG. */
function shuffleWith<T>(arr: readonly T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

interface GenerateOptions {
  /** When provided, the Pulse is fully deterministic. Same seed → same Pulse. */
  seed?: number;
  /** Force a specific category (solo rotation queue + debug). */
  forceCategoryId?: string;
  /**
   * When provided, the *content* (element pick + color pick + prompt pick)
   * is driven by this seed instead of `seed`. Solo mode uses
   * `hash(today_UTC + categoryId)` here so all replays of the same category
   * within a single UTC day surface the same 5 elements + same prompt
   * (enabling Echo Self comparisons), while `seed` stays free for the
   * pulse-id label.
   */
  contentSeed?: number;
}

/**
 * Generate a Pulse: pick category, draw 5 elements, assign colors, pick prompt.
 * In bonded mode, pass a daily seed derived from `SHA-256(date + master_seed)`
 * so all bonded players worldwide get the same Pulse on the same day. In solo
 * mode, the caller picks the category from the rotation queue and passes a
 * `contentSeed` so element/prompt selection stays stable for the day.
 */
export function generatePulse(opts: GenerateOptions = {}): Pulse {
  // Stream that decides the category when no force is given (and the pulse-id).
  const idRng = opts.seed !== undefined ? mulberry32(opts.seed) : Math.random;

  // Stream that decides element + prompt choice. Falls back to `seed` if no
  // contentSeed was supplied so legacy callers (bonded daily seed) still get
  // fully-deterministic output from a single seed.
  const contentRng =
    opts.contentSeed !== undefined
      ? mulberry32(opts.contentSeed)
      : opts.seed !== undefined
        ? mulberry32(opts.seed)
        : Math.random;
  const _shuffle =
    opts.contentSeed !== undefined || opts.seed !== undefined
      ? <T>(arr: readonly T[]) => shuffleWith(arr, contentRng)
      : shuffle;

  const category: Category =
    (opts.forceCategoryId &&
      CATEGORIES.find((c) => c.id === opts.forceCategoryId)) ||
    CATEGORIES[Math.floor(idRng() * CATEGORIES.length)]!;

  const picks = _shuffle(category.elements).slice(0, 5);

  let elements: Element[];
  if (category.needsColors) {
    const colors = _shuffle(COLOR_POOL).slice(0, 5);
    elements = picks.map((e, i) => ({ ...e, color: colors[i] }));
  } else {
    elements = picks.map((e) => ({ ...e }));
  }

  const prompt: Prompt =
    category.prompts[Math.floor(contentRng() * category.prompts.length)]!;

  // Pulse ID — short numeric for the prototype; production uses a date-based ID.
  const pulseNum =
    opts.seed !== undefined ? opts.seed % 1000 : Math.floor(idRng() * 270) + 30;
  const id = String(pulseNum).padStart(3, "0");

  return {
    id,
    categoryId: category.id,
    categoryLabel: category.label,
    elements,
    prompt,
  };
}
